import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getAdminUserFromName } from '../users/userUseCases';

/**
 * @param {object} param
 * @param {User[]} param.users
 * @param {Booking} param.booking
 * @returns {{
 * BOOKING_FORM_SCHEMA: import('@zolteam/zol-react-form/types').BasicSchema,
 * bookingDefaultValues: object,
 * splitBookingForm: Function,
 * bookingFormDataToPayload: Function
 * }}
 */
export const useBookings = ({ users, booking }) => {
  const { t } = useTranslation();

  const adminOptions = useMemo(() => [''].concat(users?.map(
    /**
     * @param {User} admin
     * @returns {string}
     */
    ({ firstname, lastname }) => `${firstname} ${lastname}`,
  )), [users]);

  const BOOKING_FORM_SCHEMA = useMemo(() => ({
    $schema: 'https://json-schema.org/draft/2020-12/schema',
    type: 'object',
    title: 'Booking',
    properties: {
      departureDate: {
        type: 'string',
        description: t('bookingForm.fields.departureDate'),
        format: 'date',
        x_layout: '1/1/1/6',
        minLength: '1',
      },
      departureTime: {
        type: 'string',
        description: t('bookingForm.fields.departureTime'),
        x_layout: '2/1/2/6',
        format: 'time',
      },
      departureUTC: {
        type: 'string',
        description: t('bookingForm.fields.departureUTC'),
        x_layout: '2/6/2/10',
      },
      arrivalDate: {
        type: 'string',
        format: 'date',
        x_layout: '3/1/3/6',
        description: t('bookingForm.fields.arrivalDate'),
        minLength: '1',
      },
      arrivalTime: {
        type: 'string',
        format: 'time',
        x_layout: '4/1/4/6',
        description: t('bookingForm.fields.arrivalTime'),
      },
      arrivalUTC: {
        type: 'string',
        description: t('bookingForm.fields.arrivalUTC'),
        x_layout: '4/6/4/10',
      },
      passengersAdults: {
        type: 'number',
        x_layout: '3/1/3/3',
        description: t('bookingForm.fields.passengersAdults'),
        min: '1',
      },
      passengersChildren: {
        type: 'number',
        x_layout: '3/3/3/5',
        description: t('bookingForm.fields.passengersChildren'),
      },
      passengersInfants: {
        type: 'number',
        x_layout: '3/5/3/7',
        description: t('bookingForm.fields.passengersInfants'),
      },
      animals: {
        type: 'number',
        x_layout: '3/7/3/9',
        description: t('bookingForm.fields.animals'),
      },
      luggageS: {
        type: 'number',
        x_layout: '4/1/4/3',
        description: t('bookingForm.fields.luggageS'),
      },
      luggageM: {
        type: 'number',
        x_layout: '4/3/4/5',
        description: t('bookingForm.fields.luggageM'),
      },
      luggageL: {
        type: 'number',
        x_layout: '4/5/4/7',
        description: t('bookingForm.fields.luggageL'),
      },
      luggageXL: {
        type: 'number',
        x_layout: '4/7/4/9',
        description: t('bookingForm.fields.luggageXL'),
      },
      requestCatering: {
        type: 'string',
        format: 'long-text',
        x_layout: '6/1/9/5',
        description: t('bookingForm.fields.requestCatering'),
      },
      requestTransport: {
        type: 'string',
        format: 'long-text',
        x_layout: '6/5/9/9',
        description: t('bookingForm.fields.requestTransport'),
      },
      requestOther: {
        type: 'string',
        format: 'long-text',
        x_layout: '6/9/9/13',
        description: t('bookingForm.fields.requestOther'),
      },
      paymentLink: {
        type: 'string',
        description: t('bookingForm.fields.paymentLink'),
        x_layout: '1/1/1/6',
      },
      paymentHoldLink: {
        type: 'string',
        description: t('bookingForm.fields.paymentHoldLink'),
        x_layout: '1/1/1/6',
      },
      cancellationReason: {
        type: 'string',
        format: 'long-text',
        x_layout: '1/1/1/13',
        description: t('bookingForm.fields.cancellationReason'),
      },
      note: {
        type: 'string',
        format: 'long-text',
        x_layout: '1/1/1/13',
        description: t('bookingForm.fields.note'),
      },
    },
    required: ['departureDate', 'arrivalDate'],
    additionalProperties: false,
  }), [adminOptions]);

  const bookingDefaultValues = useMemo(() => ({
    departureDate: booking?.departure?.date || '',
    departureTime: booking?.departure?.time || '',
    departureUTC: booking?.departure?.utc || '',
    arrivalDate: booking?.arrival?.date || '',
    arrivalTime: booking?.arrival?.time || '',
    arrivalUTC: booking?.arrival?.utc || '',
    passengersAdults: booking?.passengers?.adults?.toString() || '1',
    passengersChildren: booking?.passengers?.children?.toString() || '0',
    passengersInfants: booking?.passengers?.infants?.toString() || '0',
    animals: booking?.passengers?.animals?.toString() || '0',
    luggageS: booking?.luggage?.s?.toString() || '0',
    luggageM: booking?.luggage?.m?.toString() || '0',
    luggageL: booking?.luggage?.l?.toString() || '0',
    luggageXL: booking?.luggage?.xl?.toString() || '0',
    requestCatering: booking?.extra?.catering || '',
    requestTransport: booking?.extra?.transport || '',
    requestOther: booking?.extra?.other || '',
    userBroker: booking?.broker?.id || '',
    userDutyOfficer: booking?.dutyOfficer?.id || '',
    departureAirport: booking?.departure?.airport?.airportCode || '',
    arrivalAirport: booking?.arrival?.airport?.airportCode || '',
    paymentLink: booking?.paymentLink || '',
    paymentHoldLink: booking?.paymentHoldLink || '',
    cancellationReason: booking?.cancellationReason || '',
    note: booking?.note || '',
  }), [booking]);

  /**
   * Split the booking form into different parts
   * @param {import('@zolteam/zol-react-form/types').ConfigObject} formConfig
   * @returns {{
   * generalPart: object, departurePart: object, arrivalPart: object,
   * demandsPart: object, paymentPart: object, paymentHoldPart: object}} The split booking form
   */
  const splitBookingForm = (formConfig) => Object.entries(formConfig)
    .reduce((acc, [key, value]) => {
      if (key?.includes('cancellation')) {
        return { ...acc, generalPart: { ...acc.generalPart, [key]: value } };
      }
      if (key?.includes('departure')) {
        return { ...acc, departurePart: { ...acc.departurePart, [key]: value } };
      }
      if (key?.includes('arrival')) {
        return { ...acc, arrivalPart: { ...acc.arrivalPart, [key]: value } };
      }
      if (key === 'paymentLink') {
        return { ...acc, paymentPart: { ...acc.paymentPart, [key]: value } };
      }
      if (key === 'paymentHoldLink') {
        return { ...acc, paymentHoldPart: { ...acc.paymentHoldPart, [key]: value } };
      }
      if (key === 'note') {
        return { ...acc, notePart: { ...acc.notePart, [key]: value } };
      }
      return { ...acc, demandsPart: { ...acc.demandsPart, [key]: value } };
    }, {
      generalPart: {},
      departurePart: {},
      arrivalPart: {},
      demandsPart: {},
      paymentPart: {},
      paymentHoldPart: {},
      detailsPart: {},
      notePart: {},
    });

  /**
   * @param {object} params - The values from the form.
   * @param {any} params.formValues - The values from the form.
   * @param {{label: string, value: string}} params.departureAirport
   * @param {{label: string, value: string}} params.arrivalAirport
   * @param {{label: string, value: string}} params.customer
   * @param {string} params.status
   * @param {boolean} params.isPaymentHold
   * @returns {Booking} Payload
   */
  const bookingFormDataToPayload = ({
    formValues,
    departureAirport,
    arrivalAirport,
    customer,
    status,
    isPaymentHold,
  }) => ({
    departure: {
      airportCode: departureAirport.value,
      date: formValues.departureDate,
      time: formValues.departureTime || null,
      utc: formValues.departureUTC || null,
    },
    arrival: {
      airportCode: arrivalAirport.value,
      date: formValues.arrivalDate,
      time: formValues.arrivalTime || null,
      utc: formValues.arrivalUTC || null,
    },
    passengers: {
      adults: formValues.passengersAdults,
      children: formValues.passengersChildren,
      infants: formValues.passengersInfants,
    },
    animals: formValues.animals,
    luggage: {
      s: formValues.luggageS,
      m: formValues.luggageM,
      l: formValues.luggageL,
      xl: formValues.luggageXL,
    },
    extra: {
      catering: formValues.requestCatering || null,
      transport: formValues.requestTransport || null,
      other: formValues.requestOther || null,
    },
    dutyOfficerId: getAdminUserFromName(formValues.userDutyOfficer, users)?.id || null,
    customerId: parseInt(customer?.value, 10),
    status,
    paymentLink: isPaymentHold ? null : formValues.paymentLink,
    paymentHoldLink: isPaymentHold ? formValues.paymentHoldLink : null,
    cancellationReason: status === 'canceled' ? formValues.cancellationReason : null,
    note: formValues.note || null,
  });

  return {
    BOOKING_FORM_SCHEMA,
    bookingDefaultValues,
    splitBookingForm,
    bookingFormDataToPayload,
  };
};
